// Design System Spacing Tokens
// Consistent spacing scale following 8px grid system

export const SPACING_TOKENS = {
  spacing: {
    0: 0,
    1: '0.25rem', // 4px
    2: '0.5rem',  // 8px
    3: '0.75rem', // 12px
    4: '1rem',    // 16px
    5: '1.25rem', // 20px
    6: '1.5rem',  // 24px
    8: '2rem',    // 32px
    10: '2.5rem', // 40px
    12: '3rem',   // 48px
    16: '4rem',   // 64px
    20: '5rem',   // 80px
    24: '6rem',   // 96px
    32: '8rem',   // 128px
  },
  // Semantic spacing for common use cases
  semantic: {
    xs: '0.25rem', // 4px
    sm: '0.5rem',  // 8px
    md: '1rem',    // 16px
    lg: '1.5rem',  // 24px
    xl: '2rem',    // 32px
    xxl: '3rem',   // 48px
  },
  // Component-specific spacing
  component: {
    buttonPadding: {
      small: '0.5rem 1rem',
      medium: '0.75rem 1.5rem',
      large: '1rem 2rem',
    },
    cardPadding: {
      small: '1rem',
      medium: '1.5rem',
      large: '2rem',
    },
    sectionSpacing: {
      small: '2rem',
      medium: '3rem',
      large: '4rem',
    },
  },
} as const;

// Border radius tokens
export const BORDER_RADIUS_TOKENS = {
  borderRadius: {
    none: 0,
    sm: '0.25rem',  // 4px
    md: '0.5rem',   // 8px
    lg: '0.75rem',  // 12px
    xl: '1rem',     // 16px
    '2xl': '1.5rem', // 24px
    full: '9999px',
    // Semantic radius
    button: '0.625rem', // 10px (fazenda style)
    card: '0.75rem',    // 12px
    input: '0.625rem',  // 10px
    // Chat-specific
    bubble: '1.125rem', // 18px
    round: '1.25rem',   // 20px
  },
} as const;

// Shadow tokens
export const SHADOW_TOKENS = {
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    // Component-specific shadows
    button: '0 4px 12px rgba(12, 82, 64, 0.15)',
    card: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    appBar: '0 1px 3px rgba(30, 41, 59, 0.1)',
  },
} as const;

// Z-index tokens
export const Z_INDEX_TOKENS = {
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
} as const;

// Legacy spacing mappings for backward compatibility
export const LEGACY_SPACING = {
  xs: SPACING_TOKENS.semantic.xs,
  sm: SPACING_TOKENS.semantic.sm,
  md: SPACING_TOKENS.semantic.md,
  lg: SPACING_TOKENS.semantic.lg,
  xl: SPACING_TOKENS.semantic.xl,
} as const;

export const LEGACY_BORDER_RADIUS = {
  none: BORDER_RADIUS_TOKENS.borderRadius.none,
  sm: BORDER_RADIUS_TOKENS.borderRadius.sm,
  md: BORDER_RADIUS_TOKENS.borderRadius.md,
  lg: BORDER_RADIUS_TOKENS.borderRadius.lg,
  xl: BORDER_RADIUS_TOKENS.borderRadius.xl,
  bubble: BORDER_RADIUS_TOKENS.borderRadius.bubble,
  round: BORDER_RADIUS_TOKENS.borderRadius.round,
  full: BORDER_RADIUS_TOKENS.borderRadius.full,
} as const;